import React, { useState } from 'react';

import { Button, Input, Typography, useUiLabel } from '@canvas-logic/clp-shared-ui';

import TooltipContainer from '@/ui-kit/components/Tooltip/TooltipContainer';
import { Popover, usePopoverRef } from '@/ui-kit/components/Popover';
import { useGetShareLink } from '@/ui-kit/hooks';

import style from './ShareButton.module.css';
import { copyToClipboard } from '@/helpers/utils';

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
interface ShareButtonProps {}

const ShareButton: React.FC<ShareButtonProps> = () => {
  const titleLabel = useUiLabel('ui.shareLink.title');
  const descriptionLabel = useUiLabel('ui.shareLink.description');
  const copyButtonLabel = useUiLabel('ui.shareLink.copyButton');
  const copiedLabel = useUiLabel('ui.shareLink.copiedLabel');
  const generatingPlaceholder = useUiLabel('ui.shareLink.generatingPlaceholder');
  const { state, requestLink, link, reset } = useGetShareLink();
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [showCopyMessage, setShowCopyMessage] = useState(false);
  const popoverRef = usePopoverRef();

  const handleClick = () => {
    if (popoverOpen) {
      popoverRef.current?.close();
    } else {
      popoverRef.current?.open();
      void requestLink();
    }
  };

  const handleCopyLink = async () => {
    if (!link) {
      return;
    }

    setShowCopyMessage(true);
    setTimeout(() => {
      setShowCopyMessage(false);
    }, 3000);
    await copyToClipboard(link);
  };

  return (
    <Popover
      ref={popoverRef}
      trigger="none"
      closeOnClickOutside
      placement="bottom-start"
      padding={{
        x: -48,
      }}
      content={
        <TooltipContainer className={style.tooltipContainer} size="large">
          <div className={style.tooltipContent} data-testid="share-popover">
            <div className={style.text}>
              <div data-testid="share-popover-title">
                <Typography variant="heading6">{titleLabel}</Typography>
              </div>
              <div data-testid="share-popover-description">
                <Typography variant="paragraph-md" color="description">
                  {descriptionLabel}
                </Typography>
              </div>
            </div>
            <div className={style.controls}>
              <Input
                label=""
                placeholder={state === 'loading' ? generatingPlaceholder : undefined}
                defaultValue={link}
                readOnly
              />
              {showCopyMessage ? (
                <Button className={style.button} variant="ghost" disabled>
                  {copiedLabel}
                </Button>
              ) : (
                <Button data-copy-link-button className={style.button} variant="primary" onClick={handleCopyLink}>
                  {copyButtonLabel}
                </Button>
              )}
            </div>
          </div>
        </TooltipContainer>
      }
      animate
      onOpen={() => setPopoverOpen(true)}
      onClose={() => {
        setPopoverOpen(false);
        reset();
      }}
      useProvidedRoot
    >
      <Button
        data-testid="configurator-share-button"
        variant="ternary"
        onClick={handleClick}
        disabled={state === 'loading'}
        iconStart={popoverOpen ? 'close' : 'share'}
      />
    </Popover>
  );
};

export default ShareButton;
